import React, { useEffect, useState } from "react";
import { Ticket } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import MenuButton from "@/components/mobile/MenuButton";

/** Barcode-ish stripes for the coupon ticket. Pure SVG, no external image. */
function Barcode() {
  const bars = [3, 1, 2, 1, 4, 1, 2, 3, 1, 2, 1, 3, 1, 2, 4, 1, 2, 1, 3, 1, 2];
  let x = 0;
  return (
    <svg viewBox="0 0 100 40" width="120" height="42" aria-hidden="true">
      {bars.map((w, i) => {
        const rect = <rect key={i} x={x} y="0" width={w} height="40" fill="#0F0F10" />;
        x += w + 1;
        return rect;
      })}
    </svg>
  );
}

export default function MobileCoupons() {
  const { t, lang } = useT();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/coupons")
      .then(({ data }) => {
        if (!alive) return;
        // Support both shapes: array or { coupons: [...] }
        const list = Array.isArray(data) ? data : data?.coupons || [];
        setCoupons(list);
      })
      .catch(() => alive && setCoupons([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(lang === "ml" ? "ml-IN" : "en-GB");
    } catch {
      return "";
    }
  };

  return (
    <div className="px-5 pt-4">
      <div className="flex items-start justify-between">
        <h1 className="font-display font-extrabold text-3xl tracking-tight">{t("coupons.title")}</h1>
        <MenuButton />
      </div>

      <div className="h-px bg-border my-5" />

      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 rounded-2xl bg-secondary animate-pulse" />
          ))}
        </div>
      ) : coupons.length === 0 ? (
        <div className="grid place-items-center py-24 text-center">
          <span className="grid place-items-center w-16 h-16 rounded-full bg-secondary text-primary mb-4">
            <Ticket className="w-7 h-7" />
          </span>
          <p className="font-display font-bold text-xl">{t("coupons.none")}</p>
          <p className="text-muted-foreground text-sm mt-1">{t("coupons.claimHint")}</p>
        </div>
      ) : (
        <ul className="space-y-4" data-testid="coupons-list">
          {coupons.map((c) => {
            const pct = /(\d+)/.exec(c.discount || "")?.[1] || "10";
            const title = (c.title || c.code || "OFFER").toUpperCase();
            const shortId = (c.code || c.id || "").toString().replace(/-/g, "").slice(0, 8).toUpperCase();
            const isRedeemed = c.status === "redeemed";
            return (
              <li
                key={c.id || c.code}
                data-testid={`coupon-${c.code || c.id}`}
                className="grid grid-cols-[110px_1fr] rounded-2xl overflow-hidden border border-border shadow-[0_10px_30px_-20px_rgba(42,36,30,0.35)] bg-card"
              >
                {/* Left ticket stub */}
                <div className="relative bg-primary text-primary-foreground py-6 grid place-items-center">
                  <div className="text-center">
                    <p className="font-display font-black text-3xl leading-none">{pct}%</p>
                    <p className="text-[11px] font-extrabold tracking-widest mt-1">{t("coupons.off")}</p>
                  </div>
                  {/* Perforated edge */}
                  <div
                    className="absolute top-0 bottom-0 right-0 w-2 pointer-events-none"
                    style={{
                      backgroundImage:
                        "radial-gradient(circle at 100% 6px, hsl(var(--card)) 4px, transparent 4.5px)",
                      backgroundSize: "8px 14px",
                      backgroundRepeat: "repeat-y",
                    }}
                  />
                </div>
                {/* Right details */}
                <div className="px-4 py-3.5">
                  <p className="font-display font-extrabold text-lg leading-tight">{title}</p>
                  <div className="mt-2">
                    <Barcode />
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {isRedeemed ? `${t("coupons.redeemedOn")} ${fmtDate(c.redeemed_at || c.created_at)}` : t("coupons.active")}
                  </p>
                  {shortId && <p className="text-xs text-muted-foreground/80 mt-0.5">#{shortId}</p>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
